import { FilterFacetLayout } from '@core/enums';
import { VariantDownload } from '@stories/Widgets/ProductDetail/Downloads/Downloads';
import {
  ProductAssets,
  ProductMediaLayoutOption,
} from '@stories/Widgets/ProductDetail/ProductMediaLayout/ProductMediaLayout';
import { IRatingBreakdown } from '@stories/Widgets/ProductDetail/ProductReviews/ProductReviews.types';
import { ProductStandardsFeaturesIcons } from '@stories/Widgets/ProductDetail/ProductStandardsFeatures/ProductStandardsFeatures.api';

export type ProductDetailState = {
  modelAgilityId: number;
  modelCode: string;
  selectedVariantAgilityId: number | null;
  variants: SimpleProductVariantViewModel[];

  downloads?: VariantDownload[] | null;
  loadingDownloads: boolean;

  buckets?: SpecificationBucket[] | null;
  loadingBuckets: boolean;

  assets?: ProductAssets | null;
  loadingAssets: boolean;

  includedProducts?: IncludedProduct[] | null;
  loadingIncludedProducts: boolean;

  standardsFeaturesIcons: ProductStandardsFeaturesIcons | null;
  loadingStandardsFeaturesIcons: boolean;
  applicationIcons?: ProductStandardsFeaturesIcons | null;

  reviewBreakdown: IRatingBreakdown;

  relations: ProductRelationsCollection | null;
  loadingRelations: boolean;

  promotions: PromotionPDP[];

  // This should only be used for shared translations
  translations: Record<string, string>;

  threeKitConfiguratorUrl: string | null;
  threeKitConfiguratorButtonText: string | null;

  categories?: string[] | null;
};

export const initialProductDetailState: ProductDetailState = {
  modelAgilityId: -1,
  modelCode: '',
  selectedVariantAgilityId: null,
  variants: [],

  downloads: [],
  loadingDownloads: false,

  buckets: [],
  loadingBuckets: false,

  includedProducts: [],
  loadingIncludedProducts: false,

  assets: null,
  loadingAssets: false,

  standardsFeaturesIcons: null,
  loadingStandardsFeaturesIcons: false,

  relations: null,
  loadingRelations: false,

  threeKitConfiguratorUrl: null,
  threeKitConfiguratorButtonText: null,

  promotions: [],

  translations: {},
  reviewBreakdown: {
    average: 0,
    averagePercent: 0,
    fiveStars: 0,
    fiveStarsPercent: 0,
    fourStars: 0,
    fourStarsPercent: 0,
    threeStars: 0,
    threeStarsPercent: 0,
    twoStars: 0,
    twoStarsPercent: 0,
    oneStars: 0,
    oneStarsPercent: 0,
    totalReviews: 0,
    rawRatingAverage: 0,
  },

  categories: [],
};

export const exampleProductDetailState: ProductDetailState = {
  modelAgilityId: 542708,
  modelCode: 'M18 F2LM53',
  selectedVariantAgilityId: 348728,
  threeKitConfiguratorUrl: null,
  threeKitConfiguratorButtonText: null,
  categories: ['Category 1', 'Category 2', 'Category 3', 'Category 4', 'Category 5', 'Category 6'],
  variants: [
    {
      name: 'M18 F2LM53',
      agilityId: 348728,
      articleNumber: '4933464527',
      url: '',
      articleNumberDisplayValues: ['4933464527'],
      image: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_1.jpg',
      isSet: false,
      isNew: false,
      isComingSoon: false,
      attributeFacets: [
        {
          id: '129',
          name: 'Tool reception',
          value: '1″ square',
          rawValue: '1″ square',
          displayValue: '1″ square',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '1',
          name: 'Diameter',
          value: '5mm',
          rawValue: '5mm',
          displayValue: '5mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '2',
          name: 'Working Length',
          value: '105mm',
          rawValue: '105mm',
          displayValue: '105mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '3',
          name: 'Pack Size',
          value: '1',
          rawValue: '1',
          displayValue: '1',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '4',
          name: 'Colour',
          value: 'white',
          rawValue: '#fff',
          displayValue: 'custom.white',
          image:
            'https://staging-static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi/4932478122--Hero_1.jpg?v=ADBC9BB91BEA6746C7B946189B67BA61',
          layout: FilterFacetLayout.Thumbnails,
          hideSingleValue: true,
        },
      ],
    },
    {
      name: 'M18 F2LM53',
      agilityId: 348729,
      articleNumber: '4933464527',
      url: '',
      articleNumberDisplayValues: ['4933464527'],
      image: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_1.jpg',
      isSet: false,
      isNew: false,
      isComingSoon: false,
      attributeFacets: [
        {
          id: '129',
          name: 'Tool reception',
          value: '⅜″ square',
          rawValue: '⅜″ square',
          displayValue: '⅜″ square',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '1',
          name: 'Diameter',
          value: '5mm',
          rawValue: '5mm',
          displayValue: '5mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '2',
          name: 'Working Length',
          value: '105mm',
          rawValue: '105mm',
          displayValue: '105mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '3',
          name: 'Pack Size',
          value: '5',
          rawValue: '5',
          displayValue: '5',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: true,
        },
        {
          id: '4',
          name: 'Colour',
          value: 'black',
          rawValue: '#000',
          displayValue: 'custom.black',
          image:
            'https://staging-static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi/4932478917--Hero_1.jpg?v=08BBA483A3C354575C6AAAEF13C1B0D6',
          layout: FilterFacetLayout.Thumbnails,
          hideSingleValue: true,
        },
      ],
    },
    {
      name: 'M18 F2LM53',
      agilityId: 348727,
      articleNumber: '4933464527',
      url: '',
      articleNumberDisplayValues: ['4933464527'],
      image: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_1.jpg',
      isSet: false,
      isNew: false,
      isComingSoon: false,
      attributeFacets: [
        {
          id: '129',
          name: 'Tool reception',
          value: '½″ square',
          rawValue: '½″ square',
          displayValue: '½″ square',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '1',
          name: 'Diameter',
          value: '5mm',
          rawValue: '5mm',
          displayValue: '105mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '2',
          name: 'Working Length',
          value: '105mm',
          rawValue: '105mm',
          displayValue: '105mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '3',
          name: 'Pack Size',
          value: '10',
          rawValue: '10',
          displayValue: '10',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '4',
          name: 'Colour',
          value: 'red',
          rawValue: 'red',
          displayValue: 'custom.red',
          image:
            'https://staging-static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi/4932478916--Hero_1.jpg?v=8332B5B2DF09A6237A4C5BC61E727AD7',
          layout: FilterFacetLayout.Thumbnails,
          hideSingleValue: false,
        },
      ],
    },
    {
      name: 'M18 F2LM54',
      agilityId: 348726,
      articleNumber: null,
      url: '',
      articleNumberDisplayValues: null,
      image: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_2.jpg',
      isSet: false,
      isNew: false,
      isComingSoon: false,
      attributeFacets: [
        {
          id: '1',
          name: 'Diameter',
          value: '5mm',
          rawValue: '5mm',
          displayValue: '5mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '2',
          name: 'Working Length',
          value: '105mm',
          rawValue: '105mm',
          displayValue: '105mm',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '3',
          name: 'Pack Size',
          value: '15',
          rawValue: '15',
          displayValue: '15',
          layout: FilterFacetLayout.Buttons,
          hideSingleValue: false,
        },
        {
          id: '4',
          name: 'Colour',
          value: 'yellow',
          rawValue: 'yellow',
          displayValue: 'custom.yellow',
          image:
            'https://staging-static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi/4932478913--Hero_1.jpg?v=86560BDDBC737548D4D4E99A1256FD7F',
          layout: FilterFacetLayout.Thumbnails,
          hideSingleValue: false,
        },
      ],
    },
  ],

  assets: {
    layout: ProductMediaLayoutOption.OneAppOneHeroOneSnippet,
    hero: [
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_1.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_2.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_3.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_4.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_5.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/hero_5.jpg?q=12345',
        description: null,
        url: null,
      },
    ],
    app: [
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/app_1.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/app_2.jpg',
        description: null,
        url: null,
      },
    ],
    feature: [
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature_1.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature_2.jpg',
        description: null,
        url: null,
      },
    ],
    appNoPadding: [
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_1.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_2.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_3.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_4.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_5.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_6.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_7.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_8.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_9.jpg',
        description: null,
        url: null,
      },
      {
        imageUrl:
          'https://static.milwaukeetool.eu/remote.axd/milwaukee-media-images.s3.amazonaws.com/hi_no_padding/M18_FPD2-502X--App_10.jpg',
        description: null,
        url: null,
      },
    ],
    pack: [
      {
        imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/app_1.jpg',
        description: null,
        url: null,
      },
    ],
    snippet: {
      imageUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/snippet.mp4',
      description: null,
      url: null,
    },
    embed: {
      url: 'https://www.youtube.com/embed/n_kE7oNAIn4?autoplay=1',
      imageUrl: 'https://img.youtube.com/vi/n_kE7oNAIn4/0.jpg',
      description: null,
    },
  },
  loadingAssets: false,

  includedProducts: [
    {
      buttonText: 'View Product',
      image:
        'https://dgvcw7pll0qa8.cloudfront.net/remote.axd/milwaukee-media-images.s3.amazonaws.com/med/M18_ONEPD2-502X--Hero_1.png?v=009D4C9364D48EC8BF8E9C9EDDC5A699',
      url: '/en-gb/m18-fuel-one-key-percussion-drill/m18-onepd2/',
      modelCode: 'M18 ONEPD2',
      title: 'M18 FUEL™ ONE-KEY™ percussion drill',
      quantity: 2,
      agilityId: 1,
      attributeId: 1,
    },
  ],
  loadingIncludedProducts: false,

  buckets: [
    {
      title: 'Base Information',
      specifications: [
        { title: 'Battery pack capacity (Ah)', value: '5.0' },
        { title: 'Battery type', value: 'Li-ion' },
        { title: 'Chuck capacity (mm)', value: '13' },
        { title: 'Kit included', value: '2 x M18 B5 Battery packs, M12-18&#160;FC Charger, HD Box' },
        { title: 'Max drilling steel (mm)', value: '16' },
        { title: 'Max. drilling masonry (mm)', value: '16' },
        { title: 'Max. drilling wood (mm)', value: '89' },
        { title: 'Max. percussion rate (bpm)', value: '0 - 32,000' },
        { title: 'Max. torque (Nm)', value: '135' },
        { title: 'No load speed gear 1 (rpm)', value: '0 - 550' },
        { title: 'No load speed gear 2 (rpm)', value: '0 - 2000' },
        { title: 'No. of batteries supplied', value: '2' },
        { title: 'Speed settings', value: '2' },
        { title: 'Standard equipment', value: 'Belt clip, side handle' },
        { title: 'Supplied in', value: 'HD Box' },
        { title: 'Voltage (V)', value: '18' },
        { title: 'Weight with battery pack (EPTA) (kg)', value: '2.2 (M18 B5)' },
      ],
    },
  ],
  loadingBuckets: false,

  downloads: [
    {
      title: 'Manuals & Spare Parts',
      modelNumber: '4933464528',
      url: 'https://webservice.ttigroup.eu/partlist/index.jsp?ter=uk&lan=TT&ref=4933464528&mar=MIL&rsp=',
      imageUrl: null,
      downloadType: 0,
    },
    {
      title: 'Product Data A4',
      modelNumber: '4933464528',
      url:
        'https://api.milwaukeetool.eu/fact-tag-generator/public-generation/generate?agilityId=348728&facttagsize=1&horizontal=False&culturecode=en-GB',
      imageUrl: null,
      downloadType: 1,
    },
    {
      title: 'Product Data A4 - Landscape',
      modelNumber: '4933464528',
      url:
        'https://api.milwaukeetool.eu/fact-tag-generator/public-generation/generate?agilityId=348728&facttagsize=1&horizontal=True&culturecode=en-GB',
      imageUrl: null,
      downloadType: 1,
    },
    {
      title: 'Product Data A5',
      modelNumber: '4933464528',
      url:
        'https://api.milwaukeetool.eu/fact-tag-generator/public-generation/generate?agilityId=348728&facttagsize=2&horizontal=False&culturecode=en-GB',
      imageUrl: null,
      downloadType: 1,
    },
    {
      title: 'custom.test',
      modelNumber: '4933464528',
      url: 'https://webservice.ttigroup.eu/catalog/2021.02.00000003-M18SISH-Softwareupdate.pdf',
      imageUrl: null,
      downloadType: 1,
    },
    {
      title: 'Fact Tag 14.9 x 6.9cm',
      modelNumber: '4933464528',
      url:
        'https://api.milwaukeetool.eu/fact-tag-generator/public-generation/generate?agilityId=348728&facttagsize=4&horizontal=False&culturecode=en-GB',
      imageUrl: null,
      downloadType: 1,
    },
    {
      title: 'Fact Tag 15.5 x 8.5cm',
      modelNumber: '4933464528',
      url:
        'https://api.milwaukeetool.eu/fact-tag-generator/public-generation/generate?agilityId=348728&facttagsize=3&horizontal=False&culturecode=en-GB',
      imageUrl: null,
      downloadType: 1,
    },
  ],
  loadingDownloads: false,

  relations: null,
  loadingRelations: false,
  standardsFeaturesIcons: {
    features: [
      {
        iconText: 'Cut Resistance 1/A',
        iconUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature-icons/cut-resistance.svg',
        iconMarkup:
          '<svg xmlns="http://www.w3.org/2000/svg" width="98.118" height="126.633" viewBox="0 0 98.118 126.633">\r\n  <g id="Group_3555" data-name="Group 3555" transform="translate(-324.882 -265.623)">\r\n    <text id="_0086" data-name="0086" transform="translate(374.078 387.256)" fill="currentColor" font-size="19" font-family="SegoeUI, Segoe UI"><tspan x="-21.32" y="0" letter-spacing="0.029em">0</tspan><tspan y="0" letter-spacing="0.03em">0</tspan><tspan y="0" letter-spacing="0.029em">8</tspan><tspan y="0">6</tspan></text>\r\n    <path id="Path_3" data-name="Path 3" d="M161.469,203.515h14.068a15.569,15.569,0,0,0,15.569-15.565V158.575H181l-.112,29.251a5.739,5.739,0,0,1-5.73,5.738H162.218a5.98,5.98,0,0,1-5.977-5.985l.116-29H146.032v29.5a15.434,15.434,0,0,0,15.437,15.437" transform="translate(178.85 107.051)" fill="currentColor"/>\r\n    <path id="Path_4" data-name="Path 4" d="M146.156,200.527v14.066a15.569,15.569,0,0,0,15.565,15.569H191.1v-10.1l-29.259-.112a5.726,5.726,0,0,1-5.73-5.72V201.274A5.98,5.98,0,0,1,162.1,195.3l29.008.116V185.09H161.6a15.441,15.441,0,0,0-15.441,15.437" transform="translate(178.975 133.507)" fill="currentColor"/>\r\n    <path id="Path_5" data-name="Path 5" d="M217.562,158.656h-12.9l-21.926,17.269V158.574H172.68v44.948h10.055V186.315l21.926,17.261h12.9L189.146,181.12Z" transform="translate(205.438 107.05)" fill="currentColor"/>\r\n    <path id="Path_6" data-name="Path 6" d="M202.208,185.217H188.14a15.563,15.563,0,0,0-15.557,15.565v29.385h10.089l.042-10.213H207.37l-.046,10.213h10.324V200.658a15.44,15.44,0,0,0-15.441-15.441m5.194,24.64H182.75l.034-8.95a5.729,5.729,0,0,1,5.728-5.73h12.956a5.979,5.979,0,0,1,5.969,5.977Z" transform="translate(205.342 133.633)" fill="currentColor"/>\r\n  </g>\r\n</svg>\r\n',
      },
      {
        iconText: 'High Visibility',
        iconUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature-icons/high-visibility.svg',
        iconMarkup:
          '<svg xmlns="http://www.w3.org/2000/svg" width="98.118" height="126.633" viewBox="0 0 98.118 126.633">\r\n  <g id="Group_3555" data-name="Group 3555" transform="translate(-324.882 -265.623)">\r\n    <text id="_0086" data-name="0086" transform="translate(374.078 387.256)" fill="currentColor" font-size="19" font-family="SegoeUI, Segoe UI"><tspan x="-21.32" y="0" letter-spacing="0.029em">0</tspan><tspan y="0" letter-spacing="0.03em">0</tspan><tspan y="0" letter-spacing="0.029em">8</tspan><tspan y="0">6</tspan></text>\r\n    <path id="Path_3" data-name="Path 3" d="M161.469,203.515h14.068a15.569,15.569,0,0,0,15.569-15.565V158.575H181l-.112,29.251a5.739,5.739,0,0,1-5.73,5.738H162.218a5.98,5.98,0,0,1-5.977-5.985l.116-29H146.032v29.5a15.434,15.434,0,0,0,15.437,15.437" transform="translate(178.85 107.051)" fill="currentColor"/>\r\n    <path id="Path_4" data-name="Path 4" d="M146.156,200.527v14.066a15.569,15.569,0,0,0,15.565,15.569H191.1v-10.1l-29.259-.112a5.726,5.726,0,0,1-5.73-5.72V201.274A5.98,5.98,0,0,1,162.1,195.3l29.008.116V185.09H161.6a15.441,15.441,0,0,0-15.441,15.437" transform="translate(178.975 133.507)" fill="currentColor"/>\r\n    <path id="Path_5" data-name="Path 5" d="M217.562,158.656h-12.9l-21.926,17.269V158.574H172.68v44.948h10.055V186.315l21.926,17.261h12.9L189.146,181.12Z" transform="translate(205.438 107.05)" fill="currentColor"/>\r\n    <path id="Path_6" data-name="Path 6" d="M202.208,185.217H188.14a15.563,15.563,0,0,0-15.557,15.565v29.385h10.089l.042-10.213H207.37l-.046,10.213h10.324V200.658a15.44,15.44,0,0,0-15.441-15.441m5.194,24.64H182.75l.034-8.95a5.729,5.729,0,0,1,5.728-5.73h12.956a5.979,5.979,0,0,1,5.969,5.977Z" transform="translate(205.342 133.633)" fill="currentColor"/>\r\n  </g>\r\n</svg>\r\n',
      },
      {
        iconText: 'Comfort',
        iconUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature-icons/comfort.svg',
        iconMarkup:
          '<svg xmlns="http://www.w3.org/2000/svg" width="98.118" height="126.633" viewBox="0 0 98.118 126.633">\r\n  <g id="Group_3555" data-name="Group 3555" transform="translate(-324.882 -265.623)">\r\n    <text id="_0086" data-name="0086" transform="translate(374.078 387.256)" fill="currentColor" font-size="19" font-family="SegoeUI, Segoe UI"><tspan x="-21.32" y="0" letter-spacing="0.029em">0</tspan><tspan y="0" letter-spacing="0.03em">0</tspan><tspan y="0" letter-spacing="0.029em">8</tspan><tspan y="0">6</tspan></text>\r\n    <path id="Path_3" data-name="Path 3" d="M161.469,203.515h14.068a15.569,15.569,0,0,0,15.569-15.565V158.575H181l-.112,29.251a5.739,5.739,0,0,1-5.73,5.738H162.218a5.98,5.98,0,0,1-5.977-5.985l.116-29H146.032v29.5a15.434,15.434,0,0,0,15.437,15.437" transform="translate(178.85 107.051)" fill="currentColor"/>\r\n    <path id="Path_4" data-name="Path 4" d="M146.156,200.527v14.066a15.569,15.569,0,0,0,15.565,15.569H191.1v-10.1l-29.259-.112a5.726,5.726,0,0,1-5.73-5.72V201.274A5.98,5.98,0,0,1,162.1,195.3l29.008.116V185.09H161.6a15.441,15.441,0,0,0-15.441,15.437" transform="translate(178.975 133.507)" fill="currentColor"/>\r\n    <path id="Path_5" data-name="Path 5" d="M217.562,158.656h-12.9l-21.926,17.269V158.574H172.68v44.948h10.055V186.315l21.926,17.261h12.9L189.146,181.12Z" transform="translate(205.438 107.05)" fill="currentColor"/>\r\n    <path id="Path_6" data-name="Path 6" d="M202.208,185.217H188.14a15.563,15.563,0,0,0-15.557,15.565v29.385h10.089l.042-10.213H207.37l-.046,10.213h10.324V200.658a15.44,15.44,0,0,0-15.441-15.441m5.194,24.64H182.75l.034-8.95a5.729,5.729,0,0,1,5.728-5.73h12.956a5.979,5.979,0,0,1,5.969,5.977Z" transform="translate(205.342 133.633)" fill="currentColor"/>\r\n  </g>\r\n</svg>\r\n',
      },
    ],
    standards: [
      {
        iconText: 'ISO Accredited',
        iconUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature-icons/iso-accredited.svg',
        iconMarkup:
          '<svg xmlns="http://www.w3.org/2000/svg" width="98.118" height="126.633" viewBox="0 0 98.118 126.633">\r\n  <g id="Group_3555" data-name="Group 3555" transform="translate(-324.882 -265.623)">\r\n    <text id="_0086" data-name="0086" transform="translate(374.078 387.256)" fill="currentColor" font-size="19" font-family="SegoeUI, Segoe UI"><tspan x="-21.32" y="0" letter-spacing="0.029em">0</tspan><tspan y="0" letter-spacing="0.03em">0</tspan><tspan y="0" letter-spacing="0.029em">8</tspan><tspan y="0">6</tspan></text>\r\n    <path id="Path_3" data-name="Path 3" d="M161.469,203.515h14.068a15.569,15.569,0,0,0,15.569-15.565V158.575H181l-.112,29.251a5.739,5.739,0,0,1-5.73,5.738H162.218a5.98,5.98,0,0,1-5.977-5.985l.116-29H146.032v29.5a15.434,15.434,0,0,0,15.437,15.437" transform="translate(178.85 107.051)" fill="currentColor"/>\r\n    <path id="Path_4" data-name="Path 4" d="M146.156,200.527v14.066a15.569,15.569,0,0,0,15.565,15.569H191.1v-10.1l-29.259-.112a5.726,5.726,0,0,1-5.73-5.72V201.274A5.98,5.98,0,0,1,162.1,195.3l29.008.116V185.09H161.6a15.441,15.441,0,0,0-15.441,15.437" transform="translate(178.975 133.507)" fill="currentColor"/>\r\n    <path id="Path_5" data-name="Path 5" d="M217.562,158.656h-12.9l-21.926,17.269V158.574H172.68v44.948h10.055V186.315l21.926,17.261h12.9L189.146,181.12Z" transform="translate(205.438 107.05)" fill="currentColor"/>\r\n    <path id="Path_6" data-name="Path 6" d="M202.208,185.217H188.14a15.563,15.563,0,0,0-15.557,15.565v29.385h10.089l.042-10.213H207.37l-.046,10.213h10.324V200.658a15.44,15.44,0,0,0-15.441-15.441m5.194,24.64H182.75l.034-8.95a5.729,5.729,0,0,1,5.728-5.73h12.956a5.979,5.979,0,0,1,5.969,5.977Z" transform="translate(205.342 133.633)" fill="currentColor"/>\r\n  </g>\r\n</svg>\r\n',
      },
      {
        iconText: 'CE Approved',
        iconUrl: 'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature-icons/ce-approved.svg',
        iconMarkup:
          '<svg xmlns="http://www.w3.org/2000/svg" width="98.118" height="126.633" viewBox="0 0 98.118 126.633">\r\n  <g id="Group_3555" data-name="Group 3555" transform="translate(-324.882 -265.623)">\r\n    <text id="_0086" data-name="0086" transform="translate(374.078 387.256)" fill="currentColor" font-size="19" font-family="SegoeUI, Segoe UI"><tspan x="-21.32" y="0" letter-spacing="0.029em">0</tspan><tspan y="0" letter-spacing="0.03em">0</tspan><tspan y="0" letter-spacing="0.029em">8</tspan><tspan y="0">6</tspan></text>\r\n    <path id="Path_3" data-name="Path 3" d="M161.469,203.515h14.068a15.569,15.569,0,0,0,15.569-15.565V158.575H181l-.112,29.251a5.739,5.739,0,0,1-5.73,5.738H162.218a5.98,5.98,0,0,1-5.977-5.985l.116-29H146.032v29.5a15.434,15.434,0,0,0,15.437,15.437" transform="translate(178.85 107.051)" fill="currentColor"/>\r\n    <path id="Path_4" data-name="Path 4" d="M146.156,200.527v14.066a15.569,15.569,0,0,0,15.565,15.569H191.1v-10.1l-29.259-.112a5.726,5.726,0,0,1-5.73-5.72V201.274A5.98,5.98,0,0,1,162.1,195.3l29.008.116V185.09H161.6a15.441,15.441,0,0,0-15.441,15.437" transform="translate(178.975 133.507)" fill="currentColor"/>\r\n    <path id="Path_5" data-name="Path 5" d="M217.562,158.656h-12.9l-21.926,17.269V158.574H172.68v44.948h10.055V186.315l21.926,17.261h12.9L189.146,181.12Z" transform="translate(205.438 107.05)" fill="currentColor"/>\r\n    <path id="Path_6" data-name="Path 6" d="M202.208,185.217H188.14a15.563,15.563,0,0,0-15.557,15.565v29.385h10.089l.042-10.213H207.37l-.046,10.213h10.324V200.658a15.44,15.44,0,0,0-15.441-15.441m5.194,24.64H182.75l.034-8.95a5.729,5.729,0,0,1,5.728-5.73h12.956a5.979,5.979,0,0,1,5.969,5.977Z" transform="translate(205.342 133.633)" fill="currentColor"/>\r\n  </g>\r\n</svg>\r\n',
      },
      {
        iconText: 'UK Conformity Assessed',
        iconUrl:
          'https://milwaukee.local/NetC.MilwaukeeTools/dev/new-product-detail/feature-icons/uk-conformity-assessed.svg',
        iconMarkup:
          '<svg xmlns="http://www.w3.org/2000/svg" width="98.118" height="126.633" viewBox="0 0 98.118 126.633">\r\n  <g id="Group_3555" data-name="Group 3555" transform="translate(-324.882 -265.623)">\r\n    <text id="_0086" data-name="0086" transform="translate(374.078 387.256)" fill="currentColor" font-size="19" font-family="SegoeUI, Segoe UI"><tspan x="-21.32" y="0" letter-spacing="0.029em">0</tspan><tspan y="0" letter-spacing="0.03em">0</tspan><tspan y="0" letter-spacing="0.029em">8</tspan><tspan y="0">6</tspan></text>\r\n    <path id="Path_3" data-name="Path 3" d="M161.469,203.515h14.068a15.569,15.569,0,0,0,15.569-15.565V158.575H181l-.112,29.251a5.739,5.739,0,0,1-5.73,5.738H162.218a5.98,5.98,0,0,1-5.977-5.985l.116-29H146.032v29.5a15.434,15.434,0,0,0,15.437,15.437" transform="translate(178.85 107.051)" fill="currentColor"/>\r\n    <path id="Path_4" data-name="Path 4" d="M146.156,200.527v14.066a15.569,15.569,0,0,0,15.565,15.569H191.1v-10.1l-29.259-.112a5.726,5.726,0,0,1-5.73-5.72V201.274A5.98,5.98,0,0,1,162.1,195.3l29.008.116V185.09H161.6a15.441,15.441,0,0,0-15.441,15.437" transform="translate(178.975 133.507)" fill="currentColor"/>\r\n    <path id="Path_5" data-name="Path 5" d="M217.562,158.656h-12.9l-21.926,17.269V158.574H172.68v44.948h10.055V186.315l21.926,17.261h12.9L189.146,181.12Z" transform="translate(205.438 107.05)" fill="currentColor"/>\r\n    <path id="Path_6" data-name="Path 6" d="M202.208,185.217H188.14a15.563,15.563,0,0,0-15.557,15.565v29.385h10.089l.042-10.213H207.37l-.046,10.213h10.324V200.658a15.44,15.44,0,0,0-15.441-15.441m5.194,24.64H182.75l.034-8.95a5.729,5.729,0,0,1,5.728-5.73h12.956a5.979,5.979,0,0,1,5.969,5.977Z" transform="translate(205.342 133.633)" fill="currentColor"/>\r\n  </g>\r\n</svg>\r\n',
      },
    ],
    care: [
      {
        iconText: 'Wash at 40 degrees',
        iconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <g id="Group_32868" data-name="Group 32868" transform="translate(-84.213 -570.608)">
          <g id="Group_32867" data-name="Group 32867" transform="translate(100.639 593.083)">
            <path id="Path_19136" data-name="Path 19136" d="M151.529,640.752H114.144l-4.881-35.508,1.949-.268,4.647,33.808h33.954l4.647-33.808,1.949.268Z" transform="translate(-109.263 -604.915)" fill="#db011c"/>
            <path id="Path_19137" data-name="Path 19137" d="M112.495,612.979l-1.041-1.669a14.92,14.92,0,0,0,2.134-1.972c1.852-1.908,3.929-4.046,6.224-4.025a3.636,3.636,0,0,1,2.663,1.307c.5.477.944.967,1.376,1.44,1.947,2.134,2.6,2.573,3.99,1.526A9.668,9.668,0,0,0,129.171,608a5.527,5.527,0,0,1,3.677-2.7l.1-.013c2.744-.212,4.086,1.548,5.274,3.1a9.823,9.823,0,0,0,1.674,1.853c1.109.416,1.881-.378,3.333-2.233,1.387-1.773,3.288-4.2,6.21-2.6a17.437,17.437,0,0,1,4.118,3.685,24.163,24.163,0,0,0,2.275,2.3l-1.254,1.517a25.97,25.97,0,0,1-2.475-2.49,16.187,16.187,0,0,0-3.608-3.284c-1.327-.726-2.112.037-3.716,2.089-1.374,1.756-3.083,3.939-5.758,2.788l-.189-.108a10.871,10.871,0,0,1-2.172-2.316c-1.157-1.51-1.946-2.435-3.511-2.337-1.179.232-1.66.9-2.383,1.9a11.275,11.275,0,0,1-1.642,1.927l-.059.049c-2.993,2.289-5.031.055-6.668-1.74-.417-.457-.849-.93-1.308-1.368l-.053-.055a1.678,1.678,0,0,0-1.264-.683c-1.433,0-3.279,1.889-4.774,3.429A15.476,15.476,0,0,1,112.495,612.979Z" transform="translate(-110.018 -604.882)" fill="#db011c"/>
            <path id="Path_19138" data-name="Path 19138" d="M136.667,638.216H134.7v-2.545h-5.4V633.12l5.08-7.052h2.291V633.7h1.567v1.967h-1.567ZM131.3,633.7h3.4v-4.721Z" transform="translate(-116.16 -612.175)" fill="#db011c"/>
            <path id="Path_19139" data-name="Path 19139" d="M150.437,638.592c-1.128,0-3.134-.463-3.9-3.563a18.58,18.58,0,0,1,0-6.1c.761-3.1,2.767-3.563,3.9-3.563,1.421,0,3.257.621,3.936,3.577a18.642,18.642,0,0,1,0,6.068C153.694,637.972,151.858,638.592,150.437,638.592Zm0-11.255c-.634,0-1.53.214-1.985,2.065a17.173,17.173,0,0,0,0,5.158c.454,1.85,1.351,2.065,1.985,2.065.765,0,1.6-.244,2.019-2.05a17.086,17.086,0,0,0,0-5.188C152.041,627.581,151.2,627.337,150.437,627.337Z" transform="translate(-122.01 -611.935)" fill="#db011c"/>
          </g>
          <path id="Path_19140" data-name="Path 19140" d="M164.213,650.608h-80v-80h80ZM85.524,649.3H162.9V571.919H85.524Z" fill="#db011c"/>
        </g>
      </svg>
      `,
        iconUrl: '',
      },
      {
        iconText: 'Do not bleach',
        iconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <g id="Group_32871" data-name="Group 32871" transform="translate(-234.212 -570.608)">
          <g id="Group_32870" data-name="Group 32870" transform="translate(251.47 586.252)">
            <path id="Path_19141" data-name="Path 19141" d="M305.745,635.192H261.157l22.294-40.727Zm-41.268-1.967h37.948l-18.974-34.662Z" transform="translate(-260.746 -594.465)" fill="#db011c"/>
            <g id="Group_32869" data-name="Group 32869" transform="translate(0 7.982)">
              <rect id="Rectangle_26173" data-name="Rectangle 26173" width="55.224" height="1.967" transform="matrix(0.726, -0.687, 0.687, 0.726, 0, 37.965)" fill="#db011c"/>
              <rect id="Rectangle_26174" data-name="Rectangle 26174" width="1.967" height="55.224" transform="translate(4.028 1.433) rotate(-46.573)" fill="#db011c"/>
            </g>
          </g>
          <path id="Path_19142" data-name="Path 19142" d="M314.212,650.608h-80v-80h80ZM235.523,649.3H312.9V571.919H235.523Z" fill="#db011c"/>
        </g>
      </svg>
      `,
        iconUrl: '',
      },
      {
        iconText: 'Do not iron',
        iconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <g id="Group_32878" data-name="Group 32878" transform="translate(-534.973 -570.526)">
          <g id="Group_32877" data-name="Group 32877" transform="translate(549.759 587.591)">
            <g id="Group_32875" data-name="Group 32875" transform="translate(0 8.194)">
              <path id="Path_19146" data-name="Path 19146" d="M605.405,638.528H557.522l2.619-29.481H588.3a1.967,1.967,0,0,1,0,3.934H563.741l-1.92,21.612h43.584a1.967,1.967,0,0,1,0,3.934Z" transform="translate(-557.522 -609.047)" fill="#db011c"/>
              <path id="Path_19147" data-name="Path 19147" d="M606.458,641.136c-1.1-7.521-10.009-13.6-20.715-14.137-2.156-.108-3.864-.113-5.671-.119l-2.319-.014c-2.047-.019-4.854-.045-6.594.132a17.558,17.558,0,0,0-7.653,2.584l-.3.163-.945-1.725.3-.162a19.413,19.413,0,0,1,8.4-2.817c1.849-.188,4.718-.162,6.811-.142l2.308.014c1.832.006,3.563.011,5.764.122,11.817.594,21.306,7.246,22.564,15.816Z" transform="translate(-559.154 -614.499)" fill="#db011c"/>
            </g>
            <g id="Group_32876" data-name="Group 32876" transform="translate(2.246)">
              <rect id="Rectangle_26177" data-name="Rectangle 26177" width="62.663" height="1.967" transform="translate(0 44.478) rotate(-45.211)" fill="#db011c"/>
              <rect id="Rectangle_26178" data-name="Rectangle 26178" width="1.967" height="62.663" transform="translate(0.006 1.385) rotate(-44.774)" fill="#db011c"/>
            </g>
          </g>
          <path id="Path_19148" data-name="Path 19148" d="M614.973,650.526h-80v-80h80Zm-78.689-1.312h77.377V571.837H536.284Z" fill="#db011c"/>
        </g>
      </svg>
      `,
        iconUrl: '',
      },
      {
        iconText: 'No chemical cleaning',
        iconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <g id="Group_32881" data-name="Group 32881" transform="translate(-684.212 -570.608)">
          <g id="Group_32880" data-name="Group 32880" transform="translate(701.261 587.657)">
            <path id="Path_19149" data-name="Path 19149" d="M733.163,642.51a22.951,22.951,0,1,1,22.951-22.951A22.977,22.977,0,0,1,733.163,642.51Zm0-43.934a20.984,20.984,0,1,0,20.984,20.984A21.008,21.008,0,0,0,733.163,598.575Z" transform="translate(-710.212 -596.608)" fill="#db011c"/>
            <g id="Group_32879" data-name="Group 32879" transform="translate(0.18 0.016)">
              <rect id="Rectangle_26179" data-name="Rectangle 26179" width="62.663" height="1.967" transform="translate(0 44.478) rotate(-45.211)" fill="#db011c"/>
              <rect id="Rectangle_26180" data-name="Rectangle 26180" width="1.967" height="62.663" transform="translate(0.005 1.385) rotate(-44.775)" fill="#db011c"/>
            </g>
          </g>
          <path id="Path_19150" data-name="Path 19150" d="M764.212,650.608h-80v-80h80ZM685.523,649.3H762.9V571.919H685.523Z" fill="#db011c"/>
        </g>
      </svg>
      `,
        iconUrl: '',
      },
      {
        iconText: 'Do not tumble dry',
        iconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <g id="Group_32874" data-name="Group 32874" transform="translate(-384.212 -570.608)">
          <g id="Group_32873" data-name="Group 32873" transform="translate(401.441 587.673)">
            <path id="Path_19143" data-name="Path 19143" d="M435.917,639.854a17.7,17.7,0,1,1,17.7-17.7A17.725,17.725,0,0,1,435.917,639.854Zm0-33.443a15.738,15.738,0,1,0,15.738,15.738A15.756,15.756,0,0,0,435.917,606.411Z" transform="translate(-413.146 -599.322)" fill="#db011c"/>
            <path id="Path_19144" data-name="Path 19144" d="M455.491,641.723H412.212V598.444h43.279Zm-41.311-1.967h39.344V600.411H414.179Z" transform="translate(-411.081 -597.256)" fill="#db011c"/>
            <g id="Group_32872" data-name="Group 32872">
              <rect id="Rectangle_26175" data-name="Rectangle 26175" width="62.663" height="1.967" transform="translate(0 44.478) rotate(-45.211)" fill="#db011c"/>
              <rect id="Rectangle_26176" data-name="Rectangle 26176" width="1.967" height="62.663" transform="translate(0.005 1.385) rotate(-44.775)" fill="#db011c"/>
            </g>
          </g>
          <path id="Path_19145" data-name="Path 19145" d="M464.212,650.608h-80v-80h80ZM385.523,649.3H462.9V571.919H385.523Z" fill="#db011c"/>
        </g>
      </svg>
      `,
        iconUrl: '',
      },
    ],
    applications: [
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Excavation.svg',
        iconText: 'Excavation',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Formwork.svg',
        iconText: 'Formwork',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Curb-Installation.svg',
        iconText: 'Curb Installations',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Checking-Elevations.svg',
        iconText: 'Checking Elevations',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Excavation.svg',
        iconText: 'Checking Elevations',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Excavation.svg',
        iconText: 'Checking Elevations',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Excavation.svg',
        iconText: 'Checking Elevations',
      },
      {
        iconUrl:
          'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/MediaLibrary/Laser%20Application%20Image/Excavation.svg',
        iconText: 'Checking Elevations',
      },
    ],
  },
  loadingStandardsFeaturesIcons: false,

  promotions: [
    {
      promotionName: 'M18™ Connect up',
      promotionDescription: 'Get a FREE 1.4 M Guide Rail when you purchase a selected product',
      promotionButtonLink: 'https://www.milwaukeetool.eu',
      promotionButtonText: 'Find out More',
      promotionImageUrl: 'https://staging.milwaukeetool.eu/NetC.MilwaukeeTools/media/2024/CONNECT-UP-GRAPHIC.png',
      promotionVariants: [],
    },
  ],

  translations: {
    'custom.productdetail.variantsearchplaceholder': 'Start typing to find a product',
    'custom.productdetail.selectvariantplaceholder': 'Please select...',
    'custom.productdetail.selectedvariant': 'Currently Selected Variant',
    'custom.productdetail.selectvariantlabel': 'Select Model Variant',
    'custom.productdetail.noresults': 'No Results',
  },
  reviewBreakdown: {
    average: 4.9,
    averagePercent: 99,
    fiveStars: 92,
    fiveStarsPercent: 89.32038834951457,
    fourStars: 10,
    fourStarsPercent: 9.70873786407767,
    oneStars: 0,
    oneStarsPercent: 0,
    rawRatingAverage: 50,
    threeStars: 1,
    threeStarsPercent: 0.9708737864077669,
    twoStars: 0,
    twoStarsPercent: 0,
    totalReviews: 104,
  },
};

export const SET_SELECTED_VARIANT = 'PRODUCT_DETAIL/SET_SELECTED_VARIANT';

interface SetSelectedVariantAction {
  type: typeof SET_SELECTED_VARIANT;
  payload: number;
}

export const SET_AVAILABLE_DOWNLOADS = 'PRODUCT_DETAIL/SET_AVAILABLE_DOWNLOADS';
interface SetAvailableDownloadsAction {
  type: typeof SET_AVAILABLE_DOWNLOADS;
  payload: VariantDownload[];
}

export const SET_AVAILABLE_DOWNLOADS_LOADING = 'PRODUCT_DETAIL/SET_AVAILABLE_DOWNLOADS_LOADING';
interface SetAvailableDownloadsLoadingAction {
  type: typeof SET_AVAILABLE_DOWNLOADS_LOADING;
  payload: boolean;
}

export const SET_VARIANT_SPECS = 'PRODUCT_DETAIL/SET_VARIANT_SPECS';
interface SetVariantSpecsAction {
  type: typeof SET_VARIANT_SPECS;
  payload: SpecificationBucket[];
}

export const SET_VARIANT_SPECS_LOADING = 'PRODUCT_DETAIL/SET_VARIANT_SPECS_LOADING';
interface SetVariantSpecsLoadingAction {
  type: typeof SET_VARIANT_SPECS_LOADING;
  payload: boolean;
}

export const SET_INCLUDED_PRODUCTS = 'PRODUCT_DETAIL/SET_INCLUDED_PRODUCTS';

interface SetIncludedProductsAction {
  type: typeof SET_INCLUDED_PRODUCTS;
  payload: IncludedProduct[];
}

export const SET_INCLUDED_PRODUCTS_LOADING = 'PRODUCT_DETAIL/SET_INCLUDED_PRODUCTS_LOADING';
interface SetIncludedProductsLoadingAction {
  type: typeof SET_INCLUDED_PRODUCTS_LOADING;
  payload: boolean;
}

export const SET_ASSETS = 'PRODUCT_DETAIL/SET_ASSETS';

interface SetAssetsProductsAction {
  type: typeof SET_ASSETS;
  payload: ProductAssets;
}

export const SET_ASSETS_LOADING = 'PRODUCT_DETAIL/SET_ASSETS_LOADING';
interface SetAssetsLoadingAction {
  type: typeof SET_ASSETS_LOADING;
  payload: boolean;
}

export const SET_STANDARDS_FEATURES_ICONS = 'PRODUCT_DETAIL/SET_STANDARDS_FEATURES_ICONS';

interface SetStandardsFeaturesIconsAction {
  type: typeof SET_STANDARDS_FEATURES_ICONS;
  payload: ProductStandardsFeaturesIcons;
}

export const SET_STANDARDS_FEATURES_ICONS_LOADING = 'PRODUCT_DETAIL/SET_STANDARDS_FEATURES_ICONS_LOADING';
interface SetStandardsFeaturesIconsLoadingAction {
  type: typeof SET_STANDARDS_FEATURES_ICONS_LOADING;
  payload: boolean;
}

export const SET_RELATIONS = 'PRODUCT_DETAIL/SET_RELATIONS';

interface SetRelationsProductsAction {
  type: typeof SET_RELATIONS;
  payload: ProductRelationsCollection;
}

export const SET_RELATIONS_LOADING = 'PRODUCT_DETAIL/SET_RELATIONS_LOADING';
interface SetRelationsLoadingAction {
  type: typeof SET_RELATIONS_LOADING;
  payload: boolean;
}

export type ProductDetailAction =
  | SetSelectedVariantAction
  | SetAvailableDownloadsAction
  | SetVariantSpecsAction
  | SetIncludedProductsAction
  | SetAvailableDownloadsLoadingAction
  | SetIncludedProductsLoadingAction
  | SetVariantSpecsLoadingAction
  | SetAssetsProductsAction
  | SetAssetsLoadingAction
  | SetStandardsFeaturesIconsAction
  | SetRelationsProductsAction
  | SetRelationsLoadingAction
  | SetStandardsFeaturesIconsLoadingAction;

export default function productDetailReducer(
  state: ProductDetailState = initialProductDetailState,
  action: ProductDetailAction,
): ProductDetailState {
  switch (action.type) {
    default: {
      return state;
    }

    case SET_SELECTED_VARIANT: {
      const newState = { ...state };

      newState.selectedVariantAgilityId = action.payload;

      return {
        ...newState,
      };
    }

    case SET_AVAILABLE_DOWNLOADS: {
      const newState = { ...state };
      newState.downloads = action.payload;
      newState.loadingDownloads = false;
      return {
        ...newState,
      };
    }

    case SET_AVAILABLE_DOWNLOADS_LOADING: {
      const newState = { ...state };
      newState.loadingDownloads = action.payload;
      return { ...newState };
    }

    case SET_VARIANT_SPECS: {
      const newState = { ...state };
      newState.buckets = action.payload;
      newState.loadingBuckets = false;
      return { ...newState };
    }

    case SET_VARIANT_SPECS_LOADING: {
      const newState = { ...state };
      newState.loadingBuckets = action.payload;
      return { ...newState };
    }

    case SET_INCLUDED_PRODUCTS: {
      const newState = { ...state };
      newState.includedProducts = action.payload;
      newState.loadingIncludedProducts = false;
      return { ...newState };
    }

    case SET_INCLUDED_PRODUCTS_LOADING: {
      const newState = { ...state };
      newState.loadingIncludedProducts = action.payload;
      return { ...newState };
    }

    case SET_ASSETS: {
      const newState = { ...state };
      newState.assets = action.payload;
      newState.loadingAssets = false;
      return { ...newState };
    }

    case SET_STANDARDS_FEATURES_ICONS: {
      const newState = { ...state };
      newState.standardsFeaturesIcons = action.payload;
      newState.loadingStandardsFeaturesIcons = false;
      return { ...newState };
    }

    case SET_STANDARDS_FEATURES_ICONS_LOADING: {
      const newState = { ...state };
      newState.loadingStandardsFeaturesIcons = action.payload;
      return { ...newState };
    }

    case SET_ASSETS_LOADING: {
      const newState = { ...state };
      newState.loadingAssets = action.payload;
      return { ...newState };
    }

    case SET_RELATIONS: {
      const newState = { ...state };
      newState.relations = action.payload;
      newState.loadingAssets = false;
      return { ...newState };
    }

    case SET_RELATIONS_LOADING: {
      const newState = { ...state };
      newState.loadingRelations = action.payload;
      return { ...newState };
    }
  }
}
